<template>
  <div class="environment-manager">
    <div class="env-header">
      <span class="env-title">环境变量</span>
      <el-dropdown @command="handleEnvCommand">
        <el-button type="primary" size="small" text>
          <el-icon><More /></el-icon>
        </el-button>
        <template #dropdown>
          <el-dropdown-menu>
            <el-dropdown-item command="add">
              <el-icon><Plus /></el-icon>新建环境
            </el-dropdown-item>
            <el-dropdown-item command="import" divided>
              <el-icon><Download /></el-icon>导入环境
            </el-dropdown-item>
            <el-dropdown-item command="export">
              <el-icon><Upload /></el-icon>导出环境
            </el-dropdown-item>
          </el-dropdown-menu>
        </template>
      </el-dropdown>
    </div>

    <div class="env-selector">
      <el-select
        v-model="currentEnvId"
        placeholder="选择环境"
        clearable
        style="width: 100%"
        @change="handleEnvChange"
      >
        <el-option
          v-for="env in environments"
          :key="env.id"
          :label="env.name"
          :value="env.id"
        />
      </el-select>
    </div>

    <div v-if="currentEnv" class="env-content">
      <div class="env-info">
        <span class="env-name">{{ currentEnv.name }}</span>
        <div class="env-actions">
          <el-button type="primary" size="small" text @click="handleEditEnv">
            <el-icon><Edit /></el-icon>编辑
          </el-button>
          <el-button type="danger" size="small" text @click="handleDeleteEnv">
            <el-icon><Delete /></el-icon>删除
          </el-button>
        </div>
      </div>

      <div v-if="!currentEnv.variables || currentEnv.variables.length === 0" class="empty-variables">
        <el-empty description="暂无变量" :image-size="60">
          <el-button type="primary" size="small" @click="addVariable">
            <el-icon><Plus /></el-icon>添加变量
          </el-button>
        </el-empty>
      </div>

      <template v-else>
        <el-table :data="currentEnv.variables" size="small" style="width: 100%">
          <el-table-column prop="key" label="Key" min-width="120">
            <template #default="{ row }">
              <el-input v-model="row.key" size="small" placeholder="变量名" @change="handleVariableChange" />
            </template>
          </el-table-column>
          <el-table-column prop="value" label="Value" min-width="150">
            <template #default="{ row }">
              <el-input v-model="row.value" size="small" placeholder="变量值" @change="handleVariableChange" />
            </template>
          </el-table-column>
          <el-table-column prop="type" label="类型" width="100">
            <template #default="{ row }">
              <el-select v-model="row.type" size="small" @change="handleVariableChange">
                <el-option label="默认" value="default" />
                <el-option label="私密" value="secret" />
              </el-select>
            </template>
          </el-table-column>
          <el-table-column width="50">
            <template #default="{ $index }">
              <el-button type="danger" size="small" text @click="removeVariable($index)">
                <el-icon><Delete /></el-icon>
              </el-button>
            </template>
          </el-table-column>
        </el-table>

        <el-button type="primary" size="small" text class="add-variable-btn" @click="addVariable">
          <el-icon><Plus /></el-icon>添加变量
        </el-button>
      </template>
    </div>

    <el-empty v-else description="请选择或创建一个环境" />

    <!-- 环境编辑对话框 -->
    <el-dialog
      v-model="envDialogVisible"
      :title="isEditing ? '编辑环境' : '新建环境'"
      width="400px"
    >
      <el-form ref="envFormRef" :model="envForm" :rules="envRules" label-position="top">
        <el-form-item label="环境名称" prop="name">
          <el-input v-model="envForm.name" placeholder="请输入环境名称" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="envDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="saveEnv">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'

// eslint-disable-next-line no-undef
const props = defineProps({
  environments: {
    type: Array,
    default: () => []
  },
  modelValue: {
    type: String,
    default: ''
  }
})

// eslint-disable-next-line no-undef
const emit = defineEmits(['update:modelValue', 'update:environments', 'change'])

const currentEnvId = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val)
})

const currentEnv = computed(() => {
  return props.environments.find(e => e.id === currentEnvId.value)
})

const envDialogVisible = ref(false)
const isEditing = ref(false)
const envFormRef = ref(null)
const envForm = ref({
  id: '',
  name: ''
})
const envRules = {
  name: [{ required: true, message: '请输入环境名称', trigger: 'blur' }]
}

// 处理环境命令
const handleEnvCommand = (command) => {
  switch (command) {
    case 'add':
      isEditing.value = false
      envForm.value = { id: '', name: '' }
      envDialogVisible.value = true
      break
    case 'import':
      handleImportEnv()
      break
    case 'export':
      handleExportEnv()
      break
  }
}

// 保存环境
const saveEnv = async () => {
  if (!envFormRef.value) return

  await envFormRef.value.validate((valid) => {
    if (valid) {
      const envs = [...props.environments]

      if (isEditing.value) {
        const index = envs.findIndex(e => e.id === envForm.value.id)
        if (index !== -1) {
          envs[index].name = envForm.value.name
        }
      } else {
        envs.push({
          id: Date.now().toString(),
          name: envForm.value.name,
          variables: []
        })
      }

      emit('update:environments', envs)
      envDialogVisible.value = false
      ElMessage.success(isEditing.value ? '修改成功' : '创建成功')
    }
  })
}

// 编辑环境
const handleEditEnv = () => {
  if (!currentEnv.value) return
  isEditing.value = true
  envForm.value = {
    id: currentEnv.value.id,
    name: currentEnv.value.name
  }
  envDialogVisible.value = true
}

// 删除环境
const handleDeleteEnv = () => {
  if (!currentEnv.value) return

  ElMessageBox.confirm('确定要删除这个环境吗？', '提示', {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    type: 'warning'
  }).then(() => {
    const envs = props.environments.filter(e => e.id !== currentEnvId.value)
    emit('update:environments', envs)
    currentEnvId.value = ''
    ElMessage.success('删除成功')
  }).catch(() => {
    // 取消操作，不做处理
  })
}

// 添加变量
const addVariable = () => {
  if (!currentEnv.value) return

  const envs = [...props.environments]
  const env = envs.find(e => e.id === currentEnvId.value)
  if (env) {
    env.variables.push({
      key: '',
      value: '',
      type: 'default'
    })
    emit('update:environments', envs)
  }
}

// 移除变量
const removeVariable = (index) => {
  if (!currentEnv.value) return

  const envs = [...props.environments]
  const env = envs.find(e => e.id === currentEnvId.value)
  if (env && env.variables) {
    env.variables.splice(index, 1)
    emit('update:environments', envs)
  }
}

// 变量变化
const handleVariableChange = () => {
  emit('update:environments', [...props.environments])
}

// 环境切换
const handleEnvChange = (val) => {
  emit('change', val)
}

// 导入环境
const handleImportEnv = () => {
  const input = document.createElement('input')
  input.type = 'file'
  input.accept = '.json'
  input.onchange = async (e) => {
    const file = e.target.files[0]
    if (!file) return

    try {
      const text = await file.text()
      const data = JSON.parse(text)

      if (data.name && Array.isArray(data.values)) {
        const envs = [...props.environments]
        envs.push({
          id: Date.now().toString(),
          name: data.name,
          variables: data.values.map(v => ({
            key: v.key || '',
            value: v.value || '',
            type: v.type || 'default'
          }))
        })
        emit('update:environments', envs)
        ElMessage.success('导入成功')
      } else {
        ElMessage.error('无效的环境文件格式')
      }
    } catch (error) {
      ElMessage.error('导入失败: ' + error.message)
    }
  }
  input.click()
}

// 导出环境
const handleExportEnv = () => {
  if (!currentEnv.value) {
    ElMessage.warning('请先选择一个环境')
    return
  }

  const data = {
    name: currentEnv.value.name,
    values: currentEnv.value.variables.map(v => ({
      key: v.key,
      value: v.value,
      type: v.type
    }))
  }

  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `${currentEnv.value.name}.postman_environment.json`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
  ElMessage.success('导出成功')
}
</script>

<style scoped>
.environment-manager {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.env-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.env-title {
  font-weight: 600;
  font-size: 14px;
}

.env-selector {
  margin-bottom: 12px;
}

.env-content {
  flex: 1;
  overflow-y: auto;
}

.env-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background-color: #f5f7fa;
  border-radius: 4px;
  margin-bottom: 12px;
}

.env-name {
  flex: 1;
  font-weight: 600;
  font-size: 14px;
  color: #303133;
}

.env-actions {
  display: flex;
}

.empty-variables {
  padding: 20px 0;
}

.add-variable-btn {
  margin-top: 8px;
}
</style>
